<template>
    <div class="wrapper">

        <!-- Loader -->
        <Loader></Loader>

        <!-- Navigation -->
        <Navigation :currency="currency" :locality="locality" :navigation="navigation" :snippets="snippets" :slugs="slugs" :translatable="translatable" v-if="ready"></Navigation>

        <!-- Component -->
        <Component :is="instance"
                   :blocks="blocks"
                   :categories="categories"
                   :category="category"
                   :configuration="configuration"
                   :configurations="configurations"
                   :content="content"
                   :currency="currency"
                   :deliveries="deliveries"
                   :locality="locality"
                   :page="page"
                   :pages="pages"
                   :payments="payments"
                   :prices="prices"
                   :products="products"
                   :reservations="reservations"
                   :sequences="sequences"
                   :slug="slug"
                   :snippets="snippets"
                   :siblingsPrev="siblingsPrev"
                   :siblingsNext="siblingsNext"
                   :translations="translations"
                   :translatable="translatable"
                   :widgets="widgets"></Component>

        <!-- Footer -->
        <Footer :currency="currency" :locality="locality" :navigation="navigation" :snippets="snippets" :slugs="slugs" :translatable="translatable" v-if="ready"></Footer>

        <!-- Cookies -->
        <Cookies :currency="currency" :locality="locality" :snippets="snippets" :translatable="translatable"></Cookies>

    </div>
</template>

<script>
    /* Mixins */
    import { Default } from '@mixins/Default'

    /* Components */
    import Loader from '@components/App/Partials/Loader'
    import Navigation from '@components/App/Partials/Navigation'
    import Footer from '@components/App/Partials/Footer'
    import Cookies from '@components/App/Partials/Cookies'

    /* Logic */
    export default {
        name: 'Index',
        mixins: [Default],
        components: {
            'Loader': Loader,
            'Navigation': Navigation,
            'Footer': Footer,
            'Cookies': Cookies,
        },
        data: function () {
            return {
                blocks: '',
                categories: '',
                category: '',
                configuration: '',
                configurations: '',
                content: '',
                currency: '',
                deliveries: '',
                documents: '',
                locality: '',
                navigation: '',
                page: '',
                payments: '',
                photos: '',
                prices: '',
                products: '',
                pages: '',
                reservations: '',
                settings: '',
                sequences: '',
                slug: '',
                slugs: '',
                snippets: '',
                siblingsPrev: '',
                siblingsNext: '',
                translations: '',
                translatable: '',
                widgets: '',
                ready: '',
                meta: {
                    title: '',
                    author: '',
                    keywords: '',
                    description: '',
                    facebook_title: '',
                    facebook_description: '',
                    locality: '',
                    image: '',
                    url: '',
                }
            }
        },
        methods: {
            setExpiration: function () {
                let current = new Date()
                let expiration = this.$store.state.expiration
                if (expiration === null) {
                    this.$store.dispatch('setExpiration', new Date())
                } else {
                    let convertCurrent = this.Moment(current)
                    let convertExpiration = this.Moment(expiration)
                    let duration = this.Moment.duration(convertCurrent.diff(convertExpiration))
                    let minutes = duration.asMinutes()
                    if (minutes > 60) {
                        this.$store.dispatch('reinitializeData')
                    }
                }
            },
            fetchItems: function () {
                this.Axios.get('/api/v1/init', {
                    params: {
                        slug: this.$route.params.pathMatch
                    }
                }).then(response => {
                    this.blocks = response.data.content.data.blocks
                    this.categories = response.data.content.data.categories
                    this.category = response.data.content.data.category
                    this.configuration = response.data.content.data.configuration
                    this.configurations = response.data.content.data.configurations
                    this.content = response.data.content.data.content
                    this.currency = response.data.content.data.currency
                    this.deliveries = response.data.content.data.deliveries
                    this.documents = response.data.content.data.documents
                    this.locality = response.data.content.data.locality
                    this.navigation = response.data.content.data.navigation
                    this.page = response.data.content.data.page
                    this.payments = response.data.content.data.payments
                    this.photos = response.data.content.data.photos
                    this.prices = response.data.content.data.prices
                    this.products = response.data.content.data.products
                    this.pages = response.data.content.data.pages
                    this.reservations = response.data.content.data.reservations
                    this.settings = response.data.content.data.settings
                    this.sequences = response.data.content.data.sequences
                    this.slug = response.data.content.data.slug
                    this.slugs = response.data.content.data.slugs
                    this.snippets = response.data.content.data.snippets
                    this.siblingsPrev = response.data.content.data.siblingsPrev
                    this.siblingsNext = response.data.content.data.siblingsNext
                    this.translations = response.data.content.data.translations
                    this.translatable = response.data.content.data.translatable
                    this.widgets = response.data.content.data.widgets

                    /* Process Storage */
                    this.$store.dispatch('setPhotos', this.photos)
                    this.$store.dispatch('setDocuments', this.documents)
                }).catch(() => {
                    this.blocks = ''
                    this.categories = ''
                    this.category = ''
                    this.configuration = ''
                    this.configurations = ''
                    this.content = ''
                    this.currency = ''
                    this.deliveries = ''
                    this.documents = ''
                    this.locality = ''
                    this.navigation = ''
                    this.page = ''
                    this.payments = ''
                    this.photos = ''
                    this.prices = ''
                    this.products = ''
                    this.pages = ''
                    this.reservations = ''
                    this.settings = ''
                    this.sequences = ''
                    this.slug = ''
                    this.slugs = ''
                    this.snippets = ''
                    this.siblingsPrev = ''
                    this.siblingsNext = ''
                    this.translations = ''
                    this.translatable = ''
                    this.widgets = ''

                    /* Process Storage */
                    this.$store.dispatch('setPhotos', [])
                    this.$store.dispatch('setDocuments', [])
                }).finally(() => {
                    this.setMeta()
                    this.setExpiration()
                })
            }
        },
        created: function () {
            this.initialize()
            this.fetchItems()
            this.setOverflow()
            this.setWindowTop()
        },
        metaInfo: function () {
            return {
                title: this.meta.title,
                htmlAttrs: {
                    lang: this.meta.locality,
                    amp: true
                },
                meta: [
                    { name: 'author', content: this.meta.author },
                    { name: 'keywords', content: this.meta.keywords },
                    { name: 'description', content: this.meta.description },

                    { property: 'og:title', content:this.meta.facebook_title },
                    { property: 'og:site_name', content: this.meta.author },
                    { property: 'og:type', content: 'website' },
                    { property: 'og:url', content: this.meta.url },
                    { property: 'og:image', content: this.meta.image },
                    { property: 'og:description', content: this.meta.facebook_description },

                    { name: 'twitter:card', content: 'summary' },
                    { name: 'twitter:site', content: this.meta.url },
                    { name: 'twitter:title', content: this.meta.title },
                    { name: 'twitter:description', content: this.meta.description },

                    { itemprop: 'name', content: this.meta.title },
                    { itemprop: 'description', content: this.meta.description },
                    { itemprop: 'image', content: this.meta.image }
                ],
                link: [
                    { rel: 'canonical', href: this.meta.url }
                ]
            }
        },
        computed: {
            instance () {
                switch (parseInt(this.getSettings('Maintenance mode'))) {
                    case 1:
                        this.ready = false
                        return () => import('@components/App/Errors/503/Index')
                    default:
                        switch (this.page) {
                            case null:
                                this.ready = false
                                return () => import('@components/App/Errors/404/Index')
                            default:
                                this.ready = true
                                let category = this.Lodash.upperFirst(this.Lodash.camelCase(this.category.title))
                                let multiplicity = parseInt(this.page.multiplicity)
                                switch (multiplicity) {
                                    case 0:
                                        multiplicity = 'Sequence'
                                        break
                                    default:
                                        multiplicity = 'Category'
                                }
                                return () => import(`@components/App/Categories/${category}/${multiplicity}/Index`)
                        }
                }
            }
        }
    }
</script>


// WEBPACK FOOTER //
// resources/assets/js/components/App/Index.vue